import ForkLeft from "@mui/icons-material/ForkLeft";
import {
	Box,
	Stack
} from "@mui/material";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import AddConditionButton from "../buttons/AddConditionButton";
import RaisedTrashIconButton from "../buttons/RaisedTrashIconButton";
import type { Option } from "../constants";
import ComparativeOperatorsSelect from "../fields/ComparativeOperatorsSelect";
import type {
	DatabaseTableOptionV2,
	ResourceCategoryV2,
	ScreenLocationFieldOption,
	VariableGroupV2,
	VisibleGroupKey,
} from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import LogicField from "../fields/LogicField";
import type { RuleVariable } from "../hooks/useRuleVariables";
import type { ScreenLocationOption } from "../modals/CreateRuleModal";
import { getUniqueDefaultName } from "../nodes/nodeFactory";
import { getAssignmentValueFormEntityBinding } from "../utils/assignment-statement-display-utils";
import { buildFormulaVariableRef, buildScreenFieldRef, selectedFieldMetaFromFormulaVariable, selectedFieldMetaFromScreenField, type FormulaVariableRef, type ScreenFieldRef } from "../utils/data-field-type-utils";
import { inferResourceCategoryFromEntityFields, inferValueCategoryFromEntityFields, isDecisionValueEntityRow } from "../utils/decision-statement-value-utils";
import { DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE } from "../utils/node-name-uniqueness";
import { formatCollectionCurrentValueLabel } from "../utils/collection-utils";
import {
	buildScreenTableLocationIndex,
	resolveScreenTableIdFromGroups,
	resolveSuggestedScreenLocationForLoop,
} from "../utils/loop-resource-variables";
import BaseForm from "./BaseForm";
import FormStatementContainer from "./FormStatementContainer";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

type MatchType = "all" | "any";

export interface DecisionStatementRow {
	id: string;
	num: number;
	resource: string;
	databaseTableFieldId?: string;
	screenFieldId?: string;
	advancedTriggerId?: string;
	advancedTriggerName?: string;
	resourceCollectionId?: string;
	collectionName?: string;
	databaseTableFieldName?: string;
	screenFieldName?: string;
	screenTableField?: ScreenFieldRef;
	simpleVariableName?: string;
	formulaVariableName?: string;
	resourceFormulaVariableId?: string;
	resourceFormulaVariable?: FormulaVariableRef;
	dataType?: string;
	operator: string;
	/** Bound to FormEntitySelect; for literals mirrors plainTextValue. */
	value: string;
	/** Set when the value is free text (persisted as plainTextValue on save/node). */
	plainTextValue?: string;
	/** When the value side is a structured resource (not free text), mirrors resource-side fields. */
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueAdvancedTriggerId?: string;
	valueDatabaseTableFieldName?: string;
	valueScreenFieldName?: string;
	valueScreenField?: ScreenFieldRef;
	valueScreenTableField?: ScreenFieldRef;
	valueAdvancedTriggerName?: string;
	valueSimpleVariableName?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueFormulaVariableName?: string;
	valueFormulaVariable?: FormulaVariableRef;
	valueDatabaseSingleRowVariableId?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
	valueResourceCollectionId?: string;
	valueCollectionName?: string;
	/** Manager Approval Popup reaction node on root canvas (resource LHS). */
	resourceReactionNodeId?: string;
	resourceReactionName?: string;
}

export interface DecisionFormState {
	decisionName: string;
	decisionLogicExpression: string;
	matchType: MatchType;
	statements: DecisionStatementRow[];
}

export interface DecisionFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: DecisionFormState) => void;
	initialState?: Partial<DecisionFormState>;
	isSaving?: boolean;
	platformId?: string | number;
	operatorOptions?: Option[];
	resourceVariableGroups?: VariableGroupV2[];
	/** Formula variables from rule state (shown under "Formula Variable"). */
	formulaVariableOptions?: Option[];
	/** Single-row database table variables from rule state (shown under "Database Table Variable - Single Row"). */
	singleRowDatabaseVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	resourceAdvancedTriggers?: Option[];
	resourceCollections?: Option[];
	loopCollectionId?: string | number;
	loopCollectionName?: string;
	globalConstantOptions?: Option[];
	//TODO:: Remove this as all static config is now coming from the rule builder
	/** When true, comparative operators come from the rule builder (getFormMetadata), not a separate GET. */
	suppressComparativeOperatorsAutoFetch?: boolean;
	getDuplicateNameError?: (name: string) => string | undefined;
	/**
	 * Graph-wide reaction / assignment / decision names (lowercase), excluding the active node's name
	 * when editing — same pattern as LoopForm's `takenLoopNames`.
	 */
	takenReactionAssignmentDecisionNames?: Set<string>;
	/** Resolve rule variable ids when persisting value-side simple / formula / DB single-row picks. */
	ruleSimpleVariables?: RuleVariable[];
	ruleFormulaVariables?: RuleVariable[];
	ruleDatabaseSingleRowVariables?: RuleVariable[];
	ruleDatabaseMultiRowVariables?: RuleVariable[];
	/** Loop sub-canvas decision (unused for reaction list; same options as main). */
	isLoopContext?: boolean;
	/** Loop iteration type from the enclosing loop node. */
	loopIterateType?: "variable" | "table";
	/** Screen table id when the loop iterates a screen table. */
	loopScreenTableId?: string;
	/** Fallback when the loop node only has a table display name from the API. */
	loopScreenTableName?: string;
	/** Manager Approval Popup reactions from root canvas — main + loop decision forms. */
	managerApprovalReactionResourceOptions?: Option[];
	/** Platform screen locations (same list as Create Rule modal). */
	screenLocationOptions?: ScreenLocationOption[];
	/** Rule's configured screen location — excluded from "Other Screens with Fields". */
	ruleScreenLocationId?: string | number;
}

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];

const DecisionForm = (props: DecisionFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		platformId,
		operatorOptions,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		singleRowDatabaseVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		resourceAdvancedTriggers = EMPTY_OPTIONS,
		resourceCollections = EMPTY_OPTIONS,
		loopCollectionId,
		loopCollectionName,
		globalConstantOptions = EMPTY_OPTIONS,
		suppressComparativeOperatorsAutoFetch = false,
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		ruleSimpleVariables = [],
		ruleFormulaVariables = [],
		ruleDatabaseSingleRowVariables = [],
		ruleDatabaseMultiRowVariables = [],
		managerApprovalReactionResourceOptions = EMPTY_OPTIONS,
		screenLocationOptions = [],
		ruleScreenLocationId,
		isLoopContext = false,
		loopIterateType,
		loopScreenTableId,
		loopScreenTableName,
	} = props;
	const authHeader = useAuthHeader();
	const resolvedOperatorOptions = operatorOptions ?? [];

	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);
	const [screenTableLocationIndex, setScreenTableLocationIndex] = useState<
		Map<string, ScreenLocationFieldOption>
	>(() => new Map());
	const [rawTableGroups, setRawTableGroups] = useState<unknown[]>([]);
	const [ruleVariableSingleRowTables, setRuleVariableSingleRowTables] = useState<DatabaseTableOptionV2[]>([]);
	const [ruleVariableMultiRowTables, setRuleVariableMultiRowTables] = useState<DatabaseTableOptionV2[]>([]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchResourceOptions = async () => {
			try {
				const tablesResponse = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				if (cancelled) return;
				const rawGroups = tablesResponse.data?.tableGroups ?? [];
				setRawTableGroups(rawGroups);

				if (cancelled) return;
				setScreenTableLocationIndex(buildScreenTableLocationIndex(rawGroups, screenLocationOptions));

				const screenTables: { id: number; name: string }[] = [];

				for (const group of rawGroups) {
					if (group.screenTables) screenTables.push(...group.screenTables);
				}

				// TODO: inefficient, retrieve fields in the tables all at once!
				const screenFieldResults = await Promise.all(
					screenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				if (cancelled) return;
				setFetchedScreenFields(screenFieldResults.flat());
			} catch (error) {
				console.error("Error fetching resource options:", error);
			}
		};

		fetchResourceOptions();
		return () => { cancelled = true; };
	}, [open, platformId, authHeader, screenLocationOptions]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchRuleDatabaseVariableFields = async () => {
			const mapVars = async (vars: RuleVariable[]) =>
				Promise.all(
					vars.map(async (v) => {
						const tableId = v.source;
						if (!tableId) {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
						try {
							const res = await axios.get(apiRoutes.getDatabaseTableFields(tableId), { headers: authHeader });
							const fields: Option[] = (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: field.name,
								value: String(field.id),
							}));
							return { label: v.name, value: v.id, fields };
						} catch {
							return { label: v.name, value: v.id, fields: [] as Option[] };
						}
					}),
				);

			try {
				const [single, multi] = await Promise.all([
					mapVars(ruleDatabaseSingleRowVariables),
					mapVars(ruleDatabaseMultiRowVariables),
				]);
				if (cancelled) return;
				setRuleVariableSingleRowTables(single);
				setRuleVariableMultiRowTables(multi);
			} catch (error) {
				console.error("Error fetching database variable fields:", error);
			}
		};

		fetchRuleDatabaseVariableFields();
		return () => { cancelled = true; };
	}, [open, platformId, ruleDatabaseSingleRowVariables, ruleDatabaseMultiRowVariables, authHeader]);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);
	const otherScreenLocations = useMemo((): ScreenLocationFieldOption[] => {
		if (!screenLocationOptions.length) return [];
		return screenLocationOptions
			.filter((loc) => String(loc.id) !== String(ruleScreenLocationId ?? ""))
			.map((loc) => ({ id: loc.id, name: loc.name }));
	}, [screenLocationOptions, ruleScreenLocationId]);

	const ruleScreenLocationName = useMemo(() => {
		const match = screenLocationOptions.find(
			(loc) => String(loc.id) === String(ruleScreenLocationId ?? ""),
		);
		return match?.name;
	}, [screenLocationOptions, ruleScreenLocationId]);

	const currentScreenFieldIds = useMemo(
		() => new Set(mergedScreenFields.map((f) => f.value)),
		[mergedScreenFields],
	);

	const effectiveLoopScreenTableId = useMemo((): string | undefined => {
		if (loopScreenTableId != null && String(loopScreenTableId).trim() !== "") {
			return String(loopScreenTableId);
		}
		return resolveScreenTableIdFromGroups(rawTableGroups, loopScreenTableName);
	}, [loopScreenTableId, loopScreenTableName, rawTableGroups]);

	const suggestedScreenLocations = useMemo((): ScreenLocationFieldOption[] => {
		if (!isLoopContext) return [];
		return resolveSuggestedScreenLocationForLoop(
			loopIterateType ?? "variable",
			effectiveLoopScreenTableId,
			screenTableLocationIndex,
			{ screenLocationOptions, tableGroups: rawTableGroups },
		);
	}, [
		isLoopContext,
		loopIterateType,
		effectiveLoopScreenTableId,
		screenTableLocationIndex,
		screenLocationOptions,
		rawTableGroups,
	]);

	const isCollectionLoopDecision = Boolean(
		isLoopContext && loopCollectionId != null && String(loopCollectionId).trim() !== "",
	);

	const currentCollectionOptions = useMemo((): Option[] => {
		if (!isCollectionLoopDecision || !loopCollectionId) return [];
		const name = loopCollectionName?.trim() || "Collection";
		return [
			{
				label: formatCollectionCurrentValueLabel(name),
				value: String(loopCollectionId),
				dataType: resourceCollections.find((c) => c.value === String(loopCollectionId))?.dataType,
			},
		];
	}, [isCollectionLoopDecision, loopCollectionId, loopCollectionName, resourceCollections]);

	const valueCollectionOptions = useMemo((): Option[] => {
		if (!resourceCollections.length) return [];
		if (isCollectionLoopDecision && loopCollectionId != null) {
			return resourceCollections.filter((c) => String(c.value) !== String(loopCollectionId));
		}
		return resourceCollections;
	}, [resourceCollections, isCollectionLoopDecision, loopCollectionId]);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		if (resourceAdvancedTriggers.length) keys.push("advancedTriggers");
		if (isCollectionLoopDecision && currentCollectionOptions.length) {
			keys.push("currentCollection");
		}
		if (suggestedScreenLocations.length) keys.push("suggestedFields");
		const hasSimpleVariables = resourceVariableGroups.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (ruleVariableSingleRowTables.length) keys.push("singleRowDatabaseVariables");
		if (ruleVariableMultiRowTables.length) keys.push("multiRowDatabaseVariables");
		if (managerApprovalReactionResourceOptions.length) keys.push("reactions");
		if (otherScreenLocations.length) keys.push("otherScreenLocations");
		return keys;
	}, [
		mergedScreenFields,
		resourceAdvancedTriggers.length,
		isCollectionLoopDecision,
		currentCollectionOptions.length,
		suggestedScreenLocations.length,
		resourceVariableGroups,
		formulaVariableOptions.length,
		ruleVariableSingleRowTables.length,
		ruleVariableMultiRowTables.length,
		managerApprovalReactionResourceOptions.length,
		otherScreenLocations.length,
	]);

	/** Same entity groups as Resource, plus global constants (Resource intentionally omits those). */
	const valueFieldVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys = [...resourceVisibleGroups];
		if (valueCollectionOptions.length) keys.push("collections");
		if (globalConstantOptions.length) keys.push("globalConstants");
		return keys;
	}, [resourceVisibleGroups, valueCollectionOptions.length, globalConstantOptions.length]);

	const resolveDataType = (row: Partial<DecisionStatementRow>): string | undefined => {
		if (row.resourceReactionNodeId) {
			const m = managerApprovalReactionResourceOptions.find((o) => o.value === row.resourceReactionNodeId);
			return m?.dataType ?? "String";
		}
		if (row.dataType) return row.dataType;
		if (row.screenTableField?.dataFieldType?.name) return row.screenTableField.dataFieldType.name;
		const resourceValue =
			row.resource ??
			row.screenFieldId ??
			row.advancedTriggerId ??
			row.resourceCollectionId ??
			row.databaseTableFieldId;
		if (!resourceValue) return undefined;
		const simpleOpts = resourceVariableGroups.flatMap((g) => g.options);
		const simpleMatch = simpleOpts.find((o) => o.value === resourceValue);
		if (simpleMatch?.dataType) return simpleMatch.dataType;
		const formulaMatch = formulaVariableOptions.find((o) => o.value === resourceValue);
		if (formulaMatch?.dataType) return formulaMatch.dataType;
		const dbRowMatch = singleRowDatabaseVariableOptions.find((o) => o.value === resourceValue);
		if (dbRowMatch?.dataType) return dbRowMatch.dataType;
		const screenMatch = mergedScreenFields.find((f) => f.value === resourceValue);
		if (screenMatch?.dataType) return screenMatch.dataType;
		const advancedTriggerMatch = resourceAdvancedTriggers.find((t) => t.value === resourceValue);
		if (advancedTriggerMatch?.dataType) return advancedTriggerMatch.dataType;
		const collectionMatch = resourceCollections.find((c) => c.value === String(row.resourceCollectionId ?? resourceValue));
		if (collectionMatch?.dataType) return collectionMatch.dataType;
		const valueCollectionMatch = resourceCollections.find((c) =>
			c.value === String(row.valueResourceCollectionId ?? ""),
		);
		if (valueCollectionMatch?.dataType) return valueCollectionMatch.dataType;
		for (const table of ruleVariableSingleRowTables) {
			const fieldMatch = table.fields.find((f: Option) => f.value === resourceValue);
			if (fieldMatch?.dataType) return fieldMatch.dataType;
		}
		for (const table of ruleVariableMultiRowTables) {
			const fieldMatch = table.fields.find((f: Option) => f.value === resourceValue);
			if (fieldMatch?.dataType) return fieldMatch.dataType;
		}
		const reactionMatch = managerApprovalReactionResourceOptions.find((o) => o.value === resourceValue);
		if (reactionMatch?.dataType) return reactionMatch.dataType;
		return undefined;
	};

	const buildStatement = (overrides?: Partial<DecisionStatementRow>, index?: number): DecisionStatementRow => {
		return {
			id: overrides?.id ?? uuidv4(),
			num: overrides?.num ?? (index ?? 0) + 1,
			resource: overrides?.resource ?? "",
			databaseTableFieldId: overrides?.databaseTableFieldId,
			screenFieldId: overrides?.screenFieldId,
			advancedTriggerId: overrides?.advancedTriggerId,
			databaseTableFieldName: overrides?.databaseTableFieldName,
			screenFieldName: overrides?.screenFieldName,
			screenTableField: overrides?.screenTableField,
			advancedTriggerName: overrides?.advancedTriggerName,
			resourceCollectionId: overrides?.resourceCollectionId,
			collectionName: overrides?.collectionName,
			simpleVariableName: overrides?.simpleVariableName,
			formulaVariableName: overrides?.formulaVariableName,
			resourceFormulaVariableId: overrides?.resourceFormulaVariableId,
			resourceFormulaVariable: overrides?.resourceFormulaVariable,
			dataType: overrides ? resolveDataType(overrides) : undefined,
			operator: overrides?.operator ?? resolvedOperatorOptions[0]?.value ?? "",
			value: overrides?.value ?? "",
			plainTextValue: overrides?.plainTextValue,
			valueDatabaseTableFieldId: overrides?.valueDatabaseTableFieldId,
			valueScreenFieldId: overrides?.valueScreenFieldId,
			valueAdvancedTriggerId: overrides?.valueAdvancedTriggerId,
			valueDatabaseTableFieldName: overrides?.valueDatabaseTableFieldName,
			valueScreenFieldName: overrides?.valueScreenFieldName,
			valueScreenField: overrides?.valueScreenField,
			valueScreenTableField: overrides?.valueScreenTableField,
			valueAdvancedTriggerName: overrides?.valueAdvancedTriggerName,
			valueSimpleVariableName: overrides?.valueSimpleVariableName,
			valueSimpleVariableId: overrides?.valueSimpleVariableId,
			valueFormulaVariableId: overrides?.valueFormulaVariableId,
			valueFormulaVariableName: overrides?.valueFormulaVariableName,
			valueFormulaVariable: overrides?.valueFormulaVariable,
			valueDatabaseSingleRowVariableId: overrides?.valueDatabaseSingleRowVariableId,
			valueDatabaseSingleRowVariableName: overrides?.valueDatabaseSingleRowVariableName,
			valueGlobalConstant: overrides?.valueGlobalConstant,
			valueGlobalConstantName: overrides?.valueGlobalConstantName,
			valueResourceCollectionId: overrides?.valueResourceCollectionId,
			valueCollectionName: overrides?.valueCollectionName,
			resourceReactionNodeId: overrides?.resourceReactionNodeId,
			resourceReactionName: overrides?.resourceReactionName,
		};
	};

	const buildInitialState = (): DecisionFormState => ({
		decisionName: initialState?.decisionName
			? initialState.decisionName
			: takenReactionAssignmentDecisionNames
				? getUniqueDefaultName("Decision", takenReactionAssignmentDecisionNames, true)
				: "",
		decisionLogicExpression: initialState?.decisionLogicExpression ?? "1",
		matchType: initialState?.matchType ?? "all",
		statements: initialState?.statements?.length
			? initialState.statements.map((s, i) => buildStatement(s, i))
			: [buildStatement()]
	});

	const initialFormState = useMemo(buildInitialState, [
		initialState,
		operatorOptions,
		resourceVariableGroups,
		formulaVariableOptions,
		singleRowDatabaseVariableOptions,
		mergedScreenFields,
		ruleVariableSingleRowTables,
		ruleVariableMultiRowTables,
		takenReactionAssignmentDecisionNames,
	]);

	const [formState, setFormState] = useState<DecisionFormState>(initialFormState);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
		}
	}, [initialFormState, open]);

	const duplicateNameError = useMemo((): string | undefined => {
		const trimmed = formState.decisionName.trim();
		const trimmedLower = trimmed.toLowerCase();
		if (!trimmed) return undefined;
		const fromProp = getDuplicateNameError?.(trimmed);
		if (fromProp) return fromProp;
		if (
			takenReactionAssignmentDecisionNames &&
			initialState?.decisionName?.trim().toLowerCase() !== trimmedLower &&
			takenReactionAssignmentDecisionNames.has(trimmedLower)
		) {
			return DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE;
		}
		return undefined;
	}, [
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		formState.decisionName,
		initialState?.decisionName,
	]);

	const isSaveDisabled =
		!formState.decisionName.trim() ||
		Boolean(duplicateNameError) ||
		!formState.decisionLogicExpression.trim() ||
		formState.statements.some(
			(s) => !s.resource || !s.operator || s.value.length === 0
		);

	const updateStatement = (id: string, updates: Partial<Omit<DecisionStatementRow, "id">>) => {
		setFormState((previous) => ({
			...previous,
			statements: previous.statements.map((s) =>
				s.id === id ? { ...s, ...updates } : s
			)
		}));
	};

	const handleAddStatement = () => {
		setFormState((previous) => ({
			...previous,
			statements: [...previous.statements, buildStatement(undefined, previous.statements.length)]
		}));
	};

	const handleRemoveStatement = (id: string) => {
		setFormState((previous) => {
			if (previous.statements.length === 1) {
				return previous;
			}
			const filtered = previous.statements.filter((s) => s.id !== id);
			return {
				...previous,
				statements: filtered.map((s, i) => ({ ...s, num: i + 1 })),
			};
		});
	};
	const handleSave = () => {
		onSave({
			...formState,
			decisionName: formState.decisionName.trim(),
			decisionLogicExpression: formState.decisionLogicExpression.trim(),
			statements: formState.statements.map((s) => {
				const entity = isDecisionValueEntityRow(s);
				const trimmedValue = typeof s.value === "string" ? s.value.trim() : s.value;
				return {
					...s,
					value: entity ? s.value : trimmedValue,
					plainTextValue: entity ? undefined : (trimmedValue.length > 0 ? trimmedValue : undefined),
				};
			}),
		});
	};

	const renderStatementRows = () => (
		<Stack spacing={2}>
			{formState.statements.map((statement, index) => (
				<FormStatementContainer key={statement.id} num={statement.num}>
					<FormEntitySelect
						value={
							statement.resourceCollectionId ||
						statement.resourceReactionName ||
						statement.databaseTableFieldName ||
						statement.screenFieldName ||
						statement.advancedTriggerName ||
						(statement.resourceFormulaVariableId
							? statement.formulaVariableName
							: undefined) ||
						statement.simpleVariableName ||
						statement.formulaVariableName ||
						""
						}
						valueCategory={inferResourceCategoryFromEntityFields(statement)}
						onChange={(val: string, category: ResourceCategoryV2, label: string, dataType?: string, locationName?: string) => {
							const updates: Partial<Omit<DecisionStatementRow, "id">> = {
								resource: val,
								databaseTableFieldId: undefined,
								screenFieldId: undefined,
								advancedTriggerId: undefined,
								databaseTableFieldName: undefined,
								screenFieldName: undefined,
								screenTableField: undefined,
								advancedTriggerName: undefined,
								resourceCollectionId: undefined,
								collectionName: undefined,
								simpleVariableName: undefined,
								formulaVariableName: undefined,
								resourceFormulaVariableId: undefined,
								resourceFormulaVariable: undefined,
								resourceReactionNodeId: undefined,
								resourceReactionName: undefined,
								dataType,
								value: "",
								plainTextValue: undefined,
								valueDatabaseTableFieldId: undefined,
								valueScreenFieldId: undefined,
								valueAdvancedTriggerId: undefined,
								valueDatabaseTableFieldName: undefined,
								valueScreenFieldName: undefined,
								valueScreenField: undefined,
								valueScreenTableField: undefined,
								valueAdvancedTriggerName: undefined,
								valueSimpleVariableName: undefined,
								valueSimpleVariableId: undefined,
								valueFormulaVariableId: undefined,
								valueFormulaVariableName: undefined,
								valueFormulaVariable: undefined,
								valueDatabaseSingleRowVariableId: undefined,
								valueDatabaseSingleRowVariableName: undefined,
								valueGlobalConstant: undefined,
								valueGlobalConstantName: undefined,
								valueResourceCollectionId: undefined,
								valueCollectionName: undefined,
							};
							if (category === "reactionResource") {
								updates.resourceReactionNodeId = val;
								updates.resourceReactionName = label;
								updates.dataType = dataType ?? "String";
								updateStatement(statement.id, updates);
								return;
							}
							if (
								category === "singleRowDbVariableField" ||
							category === "multiRowDbVariableField"
							) {
								updates.databaseTableFieldId = val;
								updates.databaseTableFieldName = label;
							} else if (category === "screenTableField" || category === "screenField") {
								const screenRef = buildScreenFieldRef(
									label,
									dataType,
									statement.screenTableField,
									locationName,
								);
								updates.screenFieldId = val;
								updates.screenFieldName = screenRef?.name ?? label;
								updates.screenTableField = screenRef;
							} else if (category === "advancedTrigger") {
								updates.advancedTriggerId = val;
								updates.advancedTriggerName = label;
							} else if (category === "collection") {
								updates.resourceCollectionId = val;
								const fromList = resourceCollections.find((c) => c.value === val);
								updates.collectionName =
									fromList?.label ??
									(loopCollectionId != null && String(val) === String(loopCollectionId)
										? loopCollectionName
										: label);
							} else if (category === "simpleVariable") {
								updates.simpleVariableName = val;
							} else if (category === "formulaVariable") {
								const id = ruleFormulaVariables.find((v) => v.name === val)?.id ?? val;
								updates.resourceFormulaVariableId = val;
								updates.formulaVariableName = label;
								updates.resourceFormulaVariable = buildFormulaVariableRef(
									label,
									dataType,
									statement.resourceFormulaVariable,
									id,
								);
							}
							updateStatement(statement.id, updates);
						}}
						variableGroups={resourceVariableGroups}
						formulaVariables={formulaVariableOptions}
						databaseSingleRowVariableOptions={EMPTY_OPTIONS}
						singleRowDbVariables={ruleVariableSingleRowTables}
						multiRowDbVariables={ruleVariableMultiRowTables}
						screenFields={mergedScreenFields}
						advancedTriggers={resourceAdvancedTriggers}
						collections={[]}
						currentCollectionOptions={currentCollectionOptions}
						collectionCurrentValueCollectionId={
							isCollectionLoopDecision ? loopCollectionId : undefined
						}
						suggestedScreenLocations={suggestedScreenLocations}
						otherScreenLocations={otherScreenLocations}
						visibleGroups={resourceVisibleGroups}
						reactionResources={managerApprovalReactionResourceOptions}
						ruleScreenLocationName={ruleScreenLocationName}
						crossLocationPillEnhancements
						selectedFieldMeta={
							statement.screenFieldId
								? selectedFieldMetaFromScreenField(
										statement.screenTableField,
										statement.screenFieldId,
										{
											currentScreenFieldIds,
											ruleLocationName: ruleScreenLocationName,
										},
									)
								: statement.resourceFormulaVariableId || statement.formulaVariableName
									? selectedFieldMetaFromFormulaVariable(
											statement.resourceFormulaVariable ??
												buildFormulaVariableRef(
													statement.formulaVariableName,
													undefined,
													undefined,
													statement.resourceFormulaVariableId,
												),
										)
									: undefined
						}
					/>
					<ComparativeOperatorsSelect
						value={statement.operator}
						onChange={(value) => updateStatement(statement.id, { operator: value })}
						options={resolvedOperatorOptions}
						suppressAutoFetch={suppressComparativeOperatorsAutoFetch}
					/>
					<FormEntitySelect
						label="Value"
						isValueField
						visualizeEdgeWhitespace
						valueCategory={inferValueCategoryFromEntityFields(statement)}
						value={getAssignmentValueFormEntityBinding(statement)}
						onChange={(val: string, category: ResourceCategoryV2, label: string, dataType?: string, locationName?: string) => {
							const emptyEntity: Partial<Omit<DecisionStatementRow, "id">> = {
								valueDatabaseTableFieldId: undefined,
								valueScreenFieldId: undefined,
								valueAdvancedTriggerId: undefined,
								valueDatabaseTableFieldName: undefined,
								valueScreenFieldName: undefined,
								valueScreenField: undefined,
								valueScreenTableField: undefined,
								valueAdvancedTriggerName: undefined,
								valueSimpleVariableName: undefined,
								valueSimpleVariableId: undefined,
								valueFormulaVariableId: undefined,
								valueFormulaVariableName: undefined,
								valueFormulaVariable: undefined,
								valueDatabaseSingleRowVariableId: undefined,
								valueDatabaseSingleRowVariableName: undefined,
								valueGlobalConstant: undefined,
								valueGlobalConstantName: undefined,
								valueResourceCollectionId: undefined,
								valueCollectionName: undefined,
							};
							if (category === "literal") {
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: val.length > 0 ? val : undefined,
								});
								return;
							}
							const trimmed = val.trim();
							if (!trimmed) {
								updateStatement(statement.id, {
									...emptyEntity,
									value: "",
									plainTextValue: undefined,
								});
								return;
							}
							if (
								category === "singleRowDbVariableField" ||
								category === "multiRowDbVariableField"
							) {
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueDatabaseTableFieldId: val,
									valueDatabaseTableFieldName: label,
								});
								return;
							}
							if (category === "screenTableField" || category === "screenField") {
								const screenRef = buildScreenFieldRef(
									label,
									dataType,
									statement.valueScreenTableField ?? statement.valueScreenField,
									locationName,
								);
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueScreenFieldId: val,
									valueScreenFieldName: screenRef?.name ?? label,
									valueScreenField: screenRef,
									valueScreenTableField: screenRef,
								});
								return;
							}
							if (category === "advancedTrigger") {
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueAdvancedTriggerId: val,
									valueAdvancedTriggerName: label,
								});
								return;
							}
							if (category === "simpleVariable") {
								const id = ruleSimpleVariables.find((v) => v.name === val)?.id;
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueSimpleVariableId: id,
									valueSimpleVariableName: val,
								});
								return;
							}
							if (category === "formulaVariable") {
								const id = ruleFormulaVariables.find((v) => v.name === val)?.id;
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueFormulaVariableId: id,
									valueFormulaVariableName: val,
									valueFormulaVariable: buildFormulaVariableRef(
										val,
										dataType,
										statement.valueFormulaVariable,
										id,
									),
								});
								return;
							}
							if (category === "globalConstant") {
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueGlobalConstant: val,
									valueGlobalConstantName: label,
								});
								return;
							}
							if (category === "collection") {
								const fromList = resourceCollections.find((c) => c.value === val);
								updateStatement(statement.id, {
									...emptyEntity,
									value: val,
									plainTextValue: undefined,
									valueResourceCollectionId: val,
									valueCollectionName: fromList?.label ?? label,
								});
							}
						}}
						visibleGroups={valueFieldVisibleGroups}
						collections={valueCollectionOptions}
						variableGroups={resourceVariableGroups}
						formulaVariables={formulaVariableOptions}
						databaseSingleRowVariableOptions={EMPTY_OPTIONS}
						singleRowDbVariables={ruleVariableSingleRowTables}
						multiRowDbVariables={ruleVariableMultiRowTables}
						screenFields={mergedScreenFields}
						advancedTriggers={resourceAdvancedTriggers}
						suggestedScreenLocations={suggestedScreenLocations}
						otherScreenLocations={otherScreenLocations}
						globalConstants={globalConstantOptions}
						ruleScreenLocationName={ruleScreenLocationName}
						crossLocationPillEnhancements
						selectedFieldMeta={
							statement.valueScreenFieldId
								? selectedFieldMetaFromScreenField(
										statement.valueScreenTableField ?? statement.valueScreenField,
										statement.valueScreenFieldId,
										{
											currentScreenFieldIds,
											ruleLocationName: ruleScreenLocationName,
										},
									)
								: statement.valueFormulaVariableId || statement.valueFormulaVariableName
									? selectedFieldMetaFromFormulaVariable(
											statement.valueFormulaVariable ??
												buildFormulaVariableRef(
													statement.valueFormulaVariableName,
													undefined,
													undefined,
													statement.valueFormulaVariableId,
												),
										)
									: undefined
						}
					/>
					<RaisedTrashIconButton
						onClick={() => handleRemoveStatement(statement.id)}
						disabled={formState.statements.length === 1}
						aria-label={`Remove statement ${index + 1}`}
					/>
				</FormStatementContainer>
			))}
		</Stack>
	);

	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Decision"
			icon={<ForkLeft />}
			iconColor="#3F77E8"
			gradient="linear-gradient(135deg, #5BA0FF 0%, #2C66E0 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Decision"
			width={520}>
			<Stack spacing={3}>
				<CanvasFormTextField
					fullWidth
					label="Decision Name"
					value={formState.decisionName}
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) =>
						setFormState((previous) => ({
							...previous,
							decisionName: event.target.value
						}))
					}
					width="100%"
				/>
				<LogicField
					label="Decision Logic"
					value={formState.decisionLogicExpression}
					onChange={(value) =>
						setFormState((previous) => ({
							...previous,
							decisionLogicExpression: value
						}))
					}
					conditionCount={formState.statements.length}
				/>

				{renderStatementRows()}
				<Box sx={{mt: 2, textAlign: "center"}}>
					<AddConditionButton label="Add Condition" onClick={handleAddStatement} />
				</Box>
			</Stack>
		</BaseForm>
	);
};

export default DecisionForm;
